import {useCallback, useState, type RefObject} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Stack} from 'shells/layout/stack/stack';
import {TextField} from 'shells/textfield/textfield';

interface Props {
    textFieldRef: RefObject<HTMLInputElement | null>;
    onAdd: (value: string) => Promise<any>;
    onFocus: () => void;
    setErrorMessage: (errorMessage: string | null | undefined) => void;
}

export function BusinessCardWidgetHeader(props: Props) {
    const {onAdd, setErrorMessage} = props;

    const [textFieldValue, setTextFieldValue] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isInputLocked, setIsInputLocked] = useState<boolean>(false);
    const [mutationErrored, setMutationErrored] = useState<boolean>(false);

    const handleAddConfirm = useCallback(() => {
        setIsSaving(true);
        setIsInputLocked(true);
        setMutationErrored(false);
        return onAdd(textFieldValue)
            .then(() => {
                setIsSaving(false);
            })
            .catch((error) => {
                setMutationErrored(true);
                setErrorMessage(safelyGetGraphQLErrorMessage(error));
                setIsSaving(false);
                setIsInputLocked(false);
            });
    }, [onAdd, textFieldValue, setErrorMessage]);

    const handleReset = useCallback(() => {
        setTextFieldValue('');
        setErrorMessage(null);
        setMutationErrored(false);
        setIsInputLocked(false);
        setIsSaving(false);
    }, [setErrorMessage]);

    return (
        <div className='px-12 pb-4'>
            <Stack spacing={8}>
                <TextField
                    value={textFieldValue}
                    onChange={setTextFieldValue}
                    placeholder='Add email, phone, url, or address'
                    size='small'
                    onEnter={handleAddConfirm}
                    textFieldRef={props.textFieldRef}
                    // @ts-expect-error
                    disabled={isInputLocked}
                    onFocus={props.onFocus}
                />
                {textFieldValue.length > 0 && (
                    <Stack isHorizontal={true} spacing={8} justifyContent='end'>
                        <Button
                            variant='text-secondary'
                            size='small'
                            onClick={handleReset}
                            disabled={isInputLocked}
                        >
                            Cancel
                        </Button>
                        <SaveButton
                            isSaving={isSaving}
                            onClick={handleAddConfirm}
                            buttonText='Confirm'
                            savingText='Saving...'
                            resetOnComplete={true}
                            saveCompleteTimeout={0}
                            onClearSaveComplete={handleReset}
                            errorSaving={mutationErrored}
                            disabled={isInputLocked}
                        />
                    </Stack>
                )}
            </Stack>
        </div>
    );
}
